/**
 * 毎月の定額売上(Gate 収入側「定額」の型・2026-10-23 段階3)。全体設計 5章。例: 本部の事務料。
 *
 *  - 登録(ひな形)は管理者が行う。保存先は settings/fixed_revenues(権限ルール: 設定は管理者だけが書ける)
 *  - 計上は経理・管理者が月ごとにボタンで行う(画面を開いただけでは書き込まない)。中身は「売上・原価入力」と同じ(計上済み)
 *  - 同じひな形・同じ月は1回だけ: 取引の ID を fixedrev-<ひな形>-<YYYYMM> に決めて作り、すでにあれば作らない(二重計上の防止)
 *  - 計上日 = その月の指定日(末 = 月末。31日など無い日は月末)。期間(開始月・終了月)の外の月は計上しない
 *  - 金額改定は、ひな形の金額を直す(過去の月の取引は変わらない)
 *
 * 具体例は tests/unit/fixedRevenue.test.ts
 */
import { endOfMonth } from '@core/lib/date';
import { isValidAmount } from '@core/lib/money';
import type { DateStr, DayOrEnd, DepartmentId, MonthStr, Yen } from '@core/lib/types';
import type { DirectEntryInput } from '@core/transactions/revenueRepo';

export type FixedRevenue = {
  /** ひな形の ID(英数字。取引の ID に使う) */
  id: string;
  name: string;
  company_id: string;
  department_id: DepartmentId;
  account_id: string;
  sub_account_id: string;
  /** 請求先(取引先マスタ)。請求書に入れるときに使う */
  vendor_id: string | null;
  /** 税込 */
  amount: Yen;
  /** 計上日(その月の何日か。'end' = 月末) */
  day: DayOrEnd;
  /** 摘要(請求書の明細にも出る) */
  description: string;
  /** 計上を始める月・終える月(含む)。null は制限なし */
  from_month: MonthStr | null;
  until_month: MonthStr | null;
  active: boolean;
};

export function fixedRevenueTransactionId(itemId: string, month: MonthStr): string {
  return `fixedrev-${itemId}-${month.replace('-', '')}`;
}

/** その月の計上日 */
export function postingDateOf(month: MonthStr, day: DayOrEnd): DateStr {
  const last = endOfMonth(month);
  if (day === 'end') return last;
  const lastDay = Number(last.slice(8));
  return `${month}-${String(Math.min(day, lastDay)).padStart(2, '0')}`;
}

export function isInPeriod(item: Pick<FixedRevenue, 'from_month' | 'until_month' | 'active'>, month: MonthStr): boolean {
  if (!item.active) return false;
  if (item.from_month && month < item.from_month) return false;
  if (item.until_month && month > item.until_month) return false;
  return true;
}

export function validateFixedRevenue(item: FixedRevenue): string | null {
  if (!/^[a-z0-9-]{1,40}$/.test(item.id)) return 'ID は半角の英小文字・数字・ハイフンで書いてください';
  if (!item.name.trim()) return '名前を入れてください';
  if (!item.department_id) return '事業部を選んでください';
  if (!item.account_id || !item.sub_account_id) return '科目①と科目②を選んでください';
  if (!isValidAmount(item.amount)) return '金額は1円以上の整数で入れてください';
  if (item.day !== 'end' && !(Number.isInteger(item.day) && item.day >= 1 && item.day <= 31)) return '計上日は1〜31か「末」です';
  if (item.from_month && item.until_month && item.from_month > item.until_month) return '終了月が開始月より前です';
  return null;
}

/** その月に計上するもの(期間内・有効なもの)と、作る取引の ID */
export function postingsForMonth(
  items: FixedRevenue[],
  month: MonthStr,
  vendorName: (id: string) => string,
): Array<{ item: FixedRevenue; id: string; input: DirectEntryInput }> {
  return items
    .filter((i) => isInPeriod(i, month))
    .map((item) => ({
      item,
      id: fixedRevenueTransactionId(item.id, month),
      input: {
        companyId: item.company_id,
        departmentId: item.department_id,
        accountId: item.account_id,
        subAccountId: item.sub_account_id,
        hierarchyNodeId: null,
        postingDate: postingDateOf(month, item.day),
        amount: item.amount,
        counterparty: item.vendor_id ? vendorName(item.vendor_id) : '',
        description: item.description.trim() || item.name,
        vendorId: item.vendor_id,
      },
    }));
}

/** 新しいひな形の ID(既存と重ならない fr-001 形式) */
export function nextFixedRevenueId(items: Pick<FixedRevenue, 'id'>[]): string {
  const used = new Set(items.map((i) => i.id));
  for (let n = 1; ; n++) {
    const id = `fr-${String(n).padStart(3, '0')}`;
    if (!used.has(id)) return id;
  }
}
